import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/ui/dialog';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { Badge } from '@/shared/ui/badge';
import { Label } from '@/shared/ui/label';
import { Separator } from '@/shared/ui/separator';
import { CalendarDays, Mail, Phone, Users } from 'lucide-react';
import {
  formatStatusDisplay,
  getStatusColor,
} from '@/shared/lib/participant-schedule-status-display';
import {normalizeRelationshipLabelForUi} from '@/utils/participantDisplay';
import HistoryTimeline from '@/shared/components/HistoryTimeline';
import {useParticipantHistoryQuery} from '@/shared/hooks/useParticipantHistoryQuery';

interface DemographicQuestion {
  question_id: number;
  question: string;
  question_type: number;
  question_data: any;
  key: string;
}

interface Participant {
  id: string | number;
  name: string;
  email: string;
  phone?: string;
  gender: string;
  ageGroup?: string;
  status: string;
  schedule_status?: string | null;
  joinDate?: string;
  source?: string;
  sourceDetail?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
  /** "Updated At" shown in the Participants grid — a pre-formatted display
   *  string passed by callers; the dialog renders it verbatim. */
  demographics_updated_on?: string;
  [key: string]: any; // For demographic answers and other dynamic fields
}

interface ParticipantDetailsDialogProps {
  participant: Participant | null;
  isOpen: boolean;
  onClose: () => void;
  demographicQuestions?: DemographicQuestion[];
  /** Required to load the "View History" tab; omit to hide history data. */
  projectId?: string | null;
  /** Which tab to show when the dialog opens (default 'details'). */
  initialTab?: 'details' | 'history';
}

interface DerivedParticipantFields {
  source: string;
  sourceDetail: string;
  resourceType: string;
  agencyName: string;
  isFamilyGroup: boolean;
  familyRelationshipType: string | null;
  familyGroupCode: string | null;
  roleInFamily: string | null;
}

// Normalize source fields — accept both camelCase (sourceDetail) and snake_case
// (source_detail). Legacy rows stored "Contract ID:"; we now show "Contractor ID:"
// everywhere. Also derives the per-project resource + family-group fields from
// participant_demographic_answers.
const deriveParticipantFields = (
  participant: Participant,
): DerivedParticipantFields => {
  const source = participant.source ?? (participant as any).source ?? '';
  const sourceDetailRaw =
    participant.sourceDetail ?? (participant as any).source_detail ?? '';
  const sourceDetail = sourceDetailRaw
    .replace(/\bContract ID:/g, 'Contractor ID:')
    .split(',')
    .map((part: string) => part.trim())
    .filter((part: string) => part && !/^PSAP ID:/i.test(part))
    .join(', ');
  const resourceType =
    (participant as any).resource_type ??
    (participant as any).resourceType ??
    '';
  const agencyName =
    (participant as any).agency_name ??
    (participant as any).agencyName ??
    '';
  const isFamilyGroup =
    Boolean((participant as any).is_family_group) ||
    Boolean((participant as any).family_relationship_type);
  const familyRelationshipType =
    (participant as any).family_relationship_type ?? null;
  const familyGroupCode =
    (participant as any).family_group_code ??
    (participant as any).familyGroupCode ??
    null;
  const roleInFamily =
    (participant as any).role_in_family ??
    (participant as any).roleInFamily ??
    null;
  return {
    source,
    sourceDetail,
    resourceType,
    agencyName,
    isFamilyGroup,
    familyRelationshipType,
    familyGroupCode,
    roleInFamily,
  };
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return 'Not available';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

const formatAnswer = (answer: any): string => {
  if (answer === null || answer === undefined || answer === '') {
    return 'Not provided';
  }
  if (Array.isArray(answer)) {
    return answer.join(', ');
  }
  if (typeof answer === 'object') {
    return JSON.stringify(answer);
  }
  return String(answer);
};

// The "Details" tab body. Extracted so the dialog shell stays lean; all the
// per-section conditionals (family group, demographics, additional info, system
// fields) live here rather than inflating the parent component.
function ParticipantDetailsBody({
  participant,
  fields,
  demographicQuestions,
}: Readonly<{
  participant: Participant;
  fields: DerivedParticipantFields;
  demographicQuestions: DemographicQuestion[];
}>) {
  const {
    source,
    sourceDetail,
    resourceType,
    agencyName,
    isFamilyGroup,
    familyRelationshipType,
    familyGroupCode,
    roleInFamily,
  } = fields;

  return (
    <>
      {/* Participant Header */}
      <div className="rounded-lg border bg-card p-4 min-w-0">
        <div className="flex items-start gap-3">
          <Avatar className="size-12">
            <AvatarFallback className="text-base">
            {participant.name.split(' ').map(n => n[0]).join('')}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold truncate">{participant.name}</h3>
              <Badge
                variant="outline"
                className={getStatusColor(
                  participant.schedule_status || 'endorsed',
                )}
              >
                {formatStatusDisplay(
                  participant.schedule_status || 'endorsed',
                )}
              </Badge>
              {isFamilyGroup ? (
                <Badge variant="secondary" className="shrink-0 gap-1">
                  <Users className="h-3 w-3" />
                  Family group
                </Badge>
              ) : null}
            </div>
            <div className="mt-2 space-y-1.5 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0" />
                <span className="truncate">{participant.email}</span>
              </div>
              {participant.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 shrink-0" />
                  <span>{participant.phone}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isFamilyGroup ? (
        <div className="rounded-lg border bg-card p-4 min-w-0">
          <h4 className="text-sm font-semibold mb-3 text-foreground flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            Family group
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            <div className="sm:col-span-2">
              <Label className="text-xs text-muted-foreground">
                Relationship type
              </Label>
              <p className="text-sm mt-1">
                {familyRelationshipType
                  ? normalizeRelationshipLabelForUi(String(familyRelationshipType))
                  : '—'}
              </p>
            </div>
            {familyGroupCode ? (
              <div>
                <Label className="text-xs text-muted-foreground">
                  Family code
                </Label>
                <p className="text-sm mt-1 font-mono">{familyGroupCode}</p>
              </div>
            ) : null}
            {roleInFamily ? (
              <div>
                <Label className="text-xs text-muted-foreground">
                  Role in family
                </Label>
                <p className="text-sm mt-1">{roleInFamily}</p>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Basic Demographics */}
      <div className="rounded-lg border bg-card p-4 min-w-0">
        <h4 className="text-sm font-semibold mb-3 text-foreground">Basic Demographics</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <div>
            <Label className="text-xs text-muted-foreground">Gender</Label>
            <p className="text-sm mt-1">{participant.gender || 'Not provided'}</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Age Group</Label>
            <p className="text-sm mt-1">{participant.ageGroup || 'Not provided'}</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Join Date</Label>
            <p className="text-sm mt-1 flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
              {participant.joinDate || 'Not available'}
            </p>
          </div>
        </div>
      </div>

      {/* Demographic Questions & Answers */}
      {demographicQuestions.length > 0 && (
        <div className="rounded-lg border bg-card p-4 min-w-0">
            <h4 className="text-sm font-semibold mb-3 text-foreground">Demographic Responses</h4>
            <div className="space-y-4">
              {demographicQuestions.map((question) => {
                const answerKey = question.key;
                const answer = participant[answerKey];
                return (
                  <div key={question.question_id} className="pb-3 border-b last:border-0">
                    <Label className="text-xs text-muted-foreground font-medium">
                      {question.question}
                    </Label>
                    <p className="text-sm mt-1 break-words">
                      {formatAnswer(answer)}
                    </p>
                  </div>
                );
              })}
            </div>
        </div>
      )}

      {/* Additional Information - Source, Source Detail, Resource Type, Agency Name */}
      {(source || sourceDetail || resourceType || agencyName) && (
          <div className="rounded-lg border bg-card p-4 min-w-0">
            <h4 className="text-sm font-semibold mb-3 text-foreground">Additional Information</h4>
            <div className="space-y-3">
              <div className="min-w-0">
                <Label className="text-xs text-muted-foreground">Source</Label>
                <p className="text-sm mt-1 break-words">{source || '—'}</p>
              </div>
              <div className="min-w-0">
                <Label className="text-xs text-muted-foreground">Source Detail</Label>
                <p className="text-sm mt-1 break-words">{sourceDetail || '—'}</p>
              </div>
              <div className="min-w-0">
                <Label className="text-xs text-muted-foreground">Resource Type</Label>
                <p className="text-sm mt-1 break-words">{resourceType || '—'}</p>
              </div>
              <div className="min-w-0">
                <Label className="text-xs text-muted-foreground">Agency Name</Label>
                <p className="text-sm mt-1 break-words">{agencyName || '—'}</p>
              </div>
            </div>
          </div>
      )}

      {/* System Fields */}
      <div className="rounded-lg border bg-card p-4 min-w-0">
        <h4 className="text-sm font-semibold mb-3 text-foreground">System Information</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <div className="min-w-0">
            <Label className="text-xs text-muted-foreground">Participant ID</Label>
            <p className="text-sm mt-1 font-mono whitespace-nowrap overflow-x-auto">{participant.id}</p>
          </div>
          {participant.created_at && (
            <div className="min-w-0">
              <Label className="text-xs text-muted-foreground">Created At</Label>
              <p className="text-sm mt-1">{formatDate(participant.created_at)}</p>
            </div>
          )}
          {participant.demographics_updated_on && (
            <div className="min-w-0 sm:col-span-2">
              <Label className="text-xs text-muted-foreground">Last Updated At</Label>
              {/* Use the same "Updated At" value as the Participants grid
                  (demographics_updated_on). Callers pass it already
                  formatted for display, so render it as-is. */}
              <p className="text-sm mt-1">{participant.demographics_updated_on}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function ParticipantDetailsDialog({
  participant,
  isOpen,
  onClose,
  demographicQuestions = [],
  projectId = null,
  initialTab = 'details',
}: Readonly<ParticipantDetailsDialogProps>) {
  // Hooks must run unconditionally — keep above the early return.
  const participantId =
    participant?.id != null ? String(participant.id) : null;
  const history = useParticipantHistoryQuery({
    participantId,
    projectId,
    enabled: isOpen && Boolean(participantId) && Boolean(projectId),
  });

  if (!participant) return null;

  const {
    source,
    sourceDetail,
    resourceType,
    agencyName,
    isFamilyGroup,
    familyRelationshipType,
    familyGroupCode,
    roleInFamily,
  } = deriveParticipantFields(participant);

  // Opened via "View History" → show the history on its own (no Details tab).
  const historyOnly = initialTab === 'history';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="w-[760px] sm:max-w-[760px] max-h-[85vh] flex flex-col gap-0 p-0 overflow-hidden"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader className="flex-shrink-0 p-6 pb-4 border-b bg-muted/20">
          <DialogTitle className="text-xl font-semibold">
            {historyOnly ? 'Participant History' : 'Participant Details'}
          </DialogTitle>
          <DialogDescription className={historyOnly ? 'sr-only' : undefined}>
            {historyOnly
              ? 'Full activity history for this participant'
              : 'Complete information about this participant'}
          </DialogDescription>
        </DialogHeader>

        {historyOnly ? (
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-5 min-w-0 space-y-4">
            {/* Participant identity — gives the history immediate context. */}
            <div className="rounded-lg border bg-card p-4 flex items-start gap-3 min-w-0">
              <Avatar className="size-11">
                <AvatarFallback className="bg-blue-600 text-base text-white">
                  {participant.name.split(' ').map((n) => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-base font-semibold truncate">{participant.name}</h3>
                  <Badge
                    variant="outline"
                    className={getStatusColor(participant.schedule_status || 'endorsed')}
                  >
                    {formatStatusDisplay(participant.schedule_status || 'endorsed')}
                  </Badge>
                </div>
                <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail className="h-4 w-4 shrink-0" />
                  <span className="truncate">{participant.email}</span>
                </div>
                {(participant as any).participant_type ? (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {String((participant as any).participant_type)}
                  </p>
                ) : null}
              </div>
            </div>

            <HistoryTimeline
              entries={history.data ?? []}
              isLoading={history.isLoading}
              isError={history.isError}
              mode="participant"
            />
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-5 space-y-4 min-w-0">
            <ParticipantDetailsBody
              participant={participant}
              fields={{
                source,
                sourceDetail,
                resourceType,
                agencyName,
                isFamilyGroup,
                familyRelationshipType,
                familyGroupCode,
                roleInFamily,
              }}
              demographicQuestions={demographicQuestions}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

